import {
  WebFilterCriterionKind,
  WebFilterDateRangeMode,
  WebFilterTransport,
} from '@/api/generated/schemas';

const TRANSPORT_IDS = new Set<string>(Object.values(WebFilterTransport));

export type ClosedEnumIdsValues = { ids: WebFilterTransport[] };
export type CatalogIdsValues = { ids: number[] };
export type NumberRangeValues = { from: number | null; to: number | null };
export type DateRangeItem = {
  mode: WebFilterDateRangeMode;
  from: string;
  to: string;
  label: string | null;
};
export type DatesValues = {
  ranges: DateRangeItem[];
  weekend: boolean;
  holiday: boolean;
};

export type CriterionValues =
  | ClosedEnumIdsValues
  | CatalogIdsValues
  | NumberRangeValues
  | DatesValues;

export type CriterionFormValue = {
  kind: WebFilterCriterionKind;
  values: CriterionValues;
};

export type CriterionGroup = 'product' | 'sales' | 'dates';
export type CriterionEditor = 'transport' | 'catalog' | 'range' | 'seats' | 'published' | 'dates';

export interface CriterionDef {
  kind: WebFilterCriterionKind;
  group: CriterionGroup;
  editor: CriterionEditor;
}

export const CRITERION_DEFS: readonly CriterionDef[] = [
  { kind: WebFilterCriterionKind.Transport, group: 'product', editor: 'transport' },
  { kind: WebFilterCriterionKind.Nights, group: 'product', editor: 'range' },
  { kind: WebFilterCriterionKind.Destination, group: 'product', editor: 'catalog' },
  { kind: WebFilterCriterionKind.ProductTypes, group: 'product', editor: 'catalog' },
  { kind: WebFilterCriterionKind.Departure, group: 'product', editor: 'catalog' },
  { kind: WebFilterCriterionKind.Products, group: 'product', editor: 'catalog' },
  { kind: WebFilterCriterionKind.Price, group: 'sales', editor: 'range' },
  { kind: WebFilterCriterionKind.Seats, group: 'sales', editor: 'seats' },
  { kind: WebFilterCriterionKind.DaysBeforeDeparture, group: 'sales', editor: 'range' },
  { kind: WebFilterCriterionKind.PublishedDays, group: 'sales', editor: 'published' },
  { kind: WebFilterCriterionKind.Dates, group: 'dates', editor: 'dates' },
];

export const CRITERION_GROUPS: readonly CriterionGroup[] = ['product', 'sales', 'dates'];

export function emptyValues(kind: WebFilterCriterionKind): CriterionValues {
  const def = CRITERION_DEFS.find((item) => item.kind === kind);
  switch (def?.editor) {
    case 'transport':
      return { ids: [] };
    case 'catalog':
      return { ids: [] };
    case 'dates':
      return { ranges: [], weekend: false, holiday: false };
    default:
      return { from: null, to: null };
  }
}

export function emptyCriterion(kind: WebFilterCriterionKind): CriterionFormValue {
  return { kind, values: emptyValues(kind) };
}

export function sortCriteria<T extends { kind: WebFilterCriterionKind }>(criteria: T[]): T[] {
  const order = new Map(CRITERION_DEFS.map((def, index) => [def.kind, index]));
  return [...criteria].sort((a, b) => (order.get(a.kind) ?? 0) - (order.get(b.kind) ?? 0));
}

export function asCatalogIds(values: unknown): number[] {
  const ids = (values as CatalogIdsValues | undefined)?.ids;
  return Array.isArray(ids) ? ids.filter((id): id is number => typeof id === 'number') : [];
}

export function asTransportIds(values: unknown): WebFilterTransport[] {
  const ids = (values as ClosedEnumIdsValues | undefined)?.ids;
  if (!Array.isArray(ids)) return [];
  return ids.filter((id): id is WebFilterTransport => TRANSPORT_IDS.has(id));
}

export function asNumberRange(values: unknown): NumberRangeValues {
  const range = values as NumberRangeValues | undefined;
  return {
    from: typeof range?.from === 'number' ? range.from : null,
    to: typeof range?.to === 'number' ? range.to : null,
  };
}

export function asDates(values: unknown): DatesValues {
  const dates = (values ?? {}) as DatesValues;
  return {
    ranges: Array.isArray(dates.ranges)
      ? dates.ranges.map((range) => ({
          mode:
            range.mode === WebFilterDateRangeMode.Departures
              ? WebFilterDateRangeMode.Departures
              : WebFilterDateRangeMode.Overlap,
          from: range.from ?? '',
          to: range.to ?? '',
          label: range.label ?? null,
        }))
      : [],
    weekend: Boolean(dates.weekend),
    holiday: Boolean(dates.holiday),
  };
}

export type SeatsCompareOp = 'lt' | 'gt' | '';

/** UI „fewer than N“ → inclusive `{ to: N - 1 }`; „more than N“ → `{ from: N + 1 }`. */
export function seatsCompareToRange(op: SeatsCompareOp, value: number | null): NumberRangeValues {
  if (op === '' || value === null || !Number.isFinite(value)) {
    return { from: null, to: null };
  }
  if (op === 'lt') {
    return { from: null, to: Math.max(0, value - 1) };
  }
  return { from: value + 1, to: null };
}

export function rangeToSeatsCompare(values: NumberRangeValues): {
  op: SeatsCompareOp;
  value: number | null;
} {
  if (values.from == null && values.to != null) {
    return { op: 'lt', value: values.to + 1 };
  }
  if (values.to == null && values.from != null) {
    return { op: 'gt', value: Math.max(0, values.from - 1) };
  }
  return { op: '', value: null };
}

export function parseCriterionValues(kind: WebFilterCriterionKind, raw: unknown): CriterionValues {
  const fallback = emptyValues(kind);
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return fallback;
  }
  const record = raw as Record<string, unknown>;
  const def = CRITERION_DEFS.find((item) => item.kind === kind);
  if (def?.editor === 'transport') {
    const ids = Array.isArray(record['ids']) ? record['ids'] : [];
    return {
      ids: ids.filter((id): id is WebFilterTransport => TRANSPORT_IDS.has(id)),
    };
  }
  if (def?.editor === 'catalog') {
    const ids = Array.isArray(record['ids']) ? record['ids'] : [];
    return {
      ids: ids.filter(
        (id): id is number => typeof id === 'number' && Number.isInteger(id) && id > 0
      ),
    };
  }
  if (def?.editor === 'dates') {
    return asDates(record);
  }
  return {
    from: typeof record['from'] === 'number' ? record['from'] : null,
    to: typeof record['to'] === 'number' ? record['to'] : null,
  };
}
